import type { RemoteParticipant, RemoteTrack, RemoteTrackPublication } from "livekit-client";
import type {
  AudioCandidate,
  AudioProfile,
  AudioSource,
  AudioTransport,
  AudioTransportSink,
} from "../audio/audio-types";
import { ownerOf } from "../live-attributes";
import { PublicationLostError } from "../publication-lost";
import { probeAudioReceiver, setMediaClock, stampSender, type AudioLagProbe } from "../media-sync/media-sync";

/**
 * Le transport du moteur audio, sur une salle LiveKit.
 *
 * La salle est celle de la séance, celle des notes et de la vidéo :
 * `LiveKitTransport` la connecte et la prête ici. Ce fichier ne fait que
 * traduire — publications en sources, profils du moteur en réglages d'envoi —,
 * brancher la synchro sur chaque piste, et ne s'abonner qu'à ce que le moteur
 * veut entendre. Les décisions sont au moteur (`lib/live/audio/`). Voir
 * `docs/spec/audio-engine.md`.
 */

type LiveKitModule = typeof import("livekit-client");
type LiveKitRoom = InstanceType<LiveKitModule["Room"]>;

/** Ce qu'on lit d'un participant, local ou distant. */
interface Attributed {
  identity: string;
  name?: string;
  attributes: Readonly<Record<string, string>>;
}

/** Ce qu'on lit d'une publication, locale ou distante. */
interface PublishedTrack {
  trackSid: string;
  trackName?: string;
  isMuted: boolean;
}

/**
 * Comment un micro part dans la salle.
 *
 * # Le profil décide du débit et des silences
 *
 * La voix part au débit de la parole, 24 kbit/s, et ses silences ne partent pas
 * (`dtx`) : une salle où l'on se tait ne coûte presque rien. Un instrument part
 * à 96 kbit/s, silences compris : les couper mangerait la fin d'une note qui
 * s'éteint. Le profil voyage aussi dans le nom de la piste, où chaque récepteur
 * lit ce que porte le son (`profileOfTrack`).
 *
 * # Sans redondance
 *
 * LiveKit envoie par défaut, avec chaque trame de son, une copie des précédentes
 * (`red`), et le SFU sait défaire et refaire cette enveloppe. Une trame datée n'y
 * survit pas : les copies garderaient leur date à l'intérieur de l'enveloppe, là
 * où le récepteur ne la retire pas, et le décodeur la lirait comme du son. C'est
 * aussi pourquoi LiveKit s'en passe quand il chiffre de bout en bout. Les pertes
 * restent couvertes par la correction d'Opus lui-même.
 */
export function microphonePublishOptions(livekit: LiveKitModule, profile: AudioProfile) {
  const voice = profile === "voice";

  return {
    source: livekit.Track.Source.Microphone,
    name: profile,
    audioPreset: voice ? livekit.AudioPresets.speech : livekit.AudioPresets.musicHighQuality,
    dtx: voice,
    red: false,
    forceStereo: false,
  };
}

/**
 * Ce que prend un micro, lu dans le nom de sa piste. Une piste sans nom porte la
 * voix : c'est ce que prend un micro qui ne dit rien.
 */
export function profileOfTrack(trackName: string | undefined): AudioProfile {
  return trackName === "instrument" ? "instrument" : "voice";
}

interface HeldStream {
  stream: MediaStream;
  order: number;
}

export class LiveKitAudioTransport implements AudioTransport {
  private livekit: LiveKitModule | null = null;
  private room: LiveKitRoom | null = null;
  private sink: AudioTransportSink | null = null;
  /** Le micro que ce poste publie, et son profil ; `null` sans micro. */
  private microphone: { track: MediaStreamTrack; profile: AudioProfile } | null = null;
  /**
   * Un flux par piste, gardé tant qu'elle est là : c'est ce qui rend le même
   * objet d'une liste à l'autre. Avec son rang d'arrivée, pour ranger les
   * sources de la plus ancienne à la plus récente.
   */
  private readonly streams = new Map<string, HeldStream>();
  /** La mesure de chaque son reçu, par piste : seuls les sons branchés sur la synchro sont des sources. */
  private readonly probes = new Map<string, AudioLagProbe>();
  private sequence = 0;

  attach(sink: AudioTransportSink): () => void {
    this.sink = sink;

    return () => {
      if (this.sink === sink) {
        this.sink = null;
      }
    };
  }

  /**
   * Prête la salle au moteur, **avant** sa connexion : un son qui arrive pendant
   * la jointure doit déjà trouver le moteur, et savoir s'il est voulu.
   */
  bind(livekit: LiveKitModule, room: LiveKitRoom): void {
    const { RoomEvent } = livekit;

    this.livekit = livekit;
    this.room = room;

    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      this.subscribed(livekit, track, publication, participant);
    });
    // LiveKit prévient d'un désabonnement avant d'avoir détaché la piste de sa
    // publication : relue sur-le-champ, la source serait encore là.
    room.on(RoomEvent.TrackUnsubscribed, () => queueMicrotask(() => this.emit()));
    // Une piste retirée ne quitte les publications qu'après son désabonnement.
    room.on(RoomEvent.TrackUnpublished, () => this.emit());
    // Un micro en sourdine reste dans la liste, marqué, le temps de revenir.
    room.on(RoomEvent.TrackMuted, () => this.emit());
    room.on(RoomEvent.TrackUnmuted, () => this.emit());
    room.on(RoomEvent.ParticipantDisconnected, () => this.emit());
    // Un attribut qui change peut changer le propriétaire d'un son, donc ce que
    // le moteur veut entendre.
    room.on(RoomEvent.ParticipantAttributesChanged, () => {
      this.refresh();
      this.emit();
    });
    // Après une reconnexion complète, LiveKit republie nos pistes sur un
    // émetteur neuf : la datation doit l'y suivre.
    room.on(RoomEvent.LocalTrackPublished, (publication) => {
      if (publication.source === livekit.Track.Source.Microphone) {
        stampSender(publication.track?.sender, "audio");
      }

      this.emit();
    });
    room.on(RoomEvent.LocalTrackUnpublished, () => this.emit());
  }

  /** Oublie la salle, voulu ou subi : le moteur lâche tout ce qu'il tenait pour elle. */
  unbind(): void {
    this.room = null;
    this.livekit = null;
    this.microphone = null;
    this.streams.clear();
    this.probes.clear();
    this.sink?.disconnected();
  }

  async publish(track: MediaStreamTrack, profile: AudioProfile, muted: boolean): Promise<void> {
    const room = this.room;
    const livekit = this.livekit;

    if (room === null || livekit === null || room.state !== "connected") {
      throw new Error("LiveKitAudioTransport : pas de salle où publier.");
    }

    const current =
      this.microphone === null ? undefined : room.localParticipant.getTrackPublication(livekit.Track.Source.Microphone)?.audioTrack;

    // Déjà un micro de ce profil dans la salle : on change de piste, pas de
    // publication. Les autres l'entendent changer sans coupure.
    if (current !== undefined && this.microphone?.profile === profile) {
      // LiveKit rallume la piste neuve selon sa propre sourdine : la sourdine se
      // pose donc avant l'échange, pas après.
      if (muted && !current.isMuted) {
        await current.mute();
      }

      // `true` : la piste vient de l'appelant, LiveKit ne doit pas l'arrêter.
      await current.replaceTrack(track, true);

      if (!muted && current.isMuted) {
        await current.unmute();
      }

      this.microphone = { track, profile };
      this.emit();

      return;
    }

    // Un autre profil change le débit et les silences, qui se règlent à la
    // publication : l'ancien micro sort avant que le nouveau entre.
    if (current !== undefined) {
      // `false` : la piste est à l'appelant, qui l'arrêtera.
      await room.localParticipant.unpublishTrack(current, false);
    }

    // Enveloppée ici plutôt que par LiveKit, qui rallumerait une piste nue en
    // l'enveloppant : un micro en sourdine part éteint, et la salle l'apprend
    // par la demande de publication elle-même.
    const local = new livekit.LocalAudioTrack(track, undefined, true);

    try {
      if (muted) {
        await local.mute();
      }

      const publication = await room.localParticipant.publishTrack(local, microphonePublishOptions(livekit, profile));

      stampSender(publication.track?.sender, "audio");
    } catch (error) {
      // L'ancien micro est déjà sorti : plus rien n'est publié, et le moteur doit
      // le savoir pour ne pas le croire encore en salle.
      if (current !== undefined) {
        this.microphone = null;
        this.emit();

        throw new PublicationLostError(error);
      }

      throw error;
    }

    this.microphone = { track, profile };
    this.emit();
  }

  async setMuted(muted: boolean): Promise<void> {
    const room = this.room;
    const livekit = this.livekit;

    if (room === null || livekit === null || this.microphone === null) {
      return;
    }

    const publication = room.localParticipant.getTrackPublication(livekit.Track.Source.Microphone);

    if (publication === undefined) {
      return;
    }

    if (muted) {
      await publication.mute();
    } else {
      await publication.unmute();
    }

    this.emit();
  }

  async unpublish(): Promise<void> {
    const room = this.room;
    const livekit = this.livekit;
    const microphone = this.microphone;

    this.microphone = null;

    if (room === null || livekit === null || microphone === null) {
      return;
    }

    const published = room.localParticipant.getTrackPublication(livekit.Track.Source.Microphone)?.track;

    // `false` : la piste est à l'appelant, qui l'a ouverte et l'arrêtera.
    await room.localParticipant.unpublishTrack(published ?? microphone.track, false);
    this.emit();
  }

  setClock(clockOffsetMs: number | null): void {
    setMediaClock(clockOffsetMs);
  }

  /**
   * S'abonne à ce que le moteur veut entendre, et se désabonne du reste : un son
   * qu'on n'écoute pas n'a pas à traverser le réseau. Un son qui n'est pas un
   * micro n'est jamais voulu.
   */
  refresh(): void {
    const room = this.room;
    const livekit = this.livekit;
    const sink = this.sink;

    if (room === null || livekit === null || sink === null) {
      return;
    }

    for (const participant of room.remoteParticipants.values()) {
      for (const publication of participant.audioTrackPublications.values()) {
        const wanted =
          publication.source === livekit.Track.Source.Microphone && sink.wants(candidateOf(participant, publication));

        if (publication.isDesired !== wanted) {
          publication.setSubscribed(wanted);
        }
      }
    }
  }

  lagOf(sourceId: string): number | null {
    return this.probes.get(sourceId)?.lagMs() ?? null;
  }

  localOwner(): string | null {
    const local = this.room?.localParticipant;

    if (local === undefined || local.identity === "") {
      return null;
    }

    return ownerOf(local.identity, local.attributes);
  }

  private subscribed(
    livekit: LiveKitModule,
    track: RemoteTrack,
    publication: RemoteTrackPublication,
    participant: RemoteParticipant,
  ): void {
    // Les images sont au transport vidéo (`livekit-video.ts`).
    if (track.kind !== livekit.Track.Kind.Audio) {
      return;
    }

    // Un son que le moteur ne veut pas entendre — le contexte n'écoute pas, ou
    // il est à ce poste — n'a pas à traverser le réseau. Un son qui n'est pas un
    // micro, celui d'un partage d'écran, échapperait à la synchro : sous
    // Chromium, où la connexion ouvre les flux insérables, il ne passerait pas.
    if (
      publication.source !== livekit.Track.Source.Microphone ||
      !(this.sink?.wants(candidateOf(participant, publication)) ?? false)
    ) {
      publication.setSubscribed(false);

      return;
    }

    const probe = probeAudioReceiver(track.receiver);

    // Un son daté qu'on ne saurait pas dédater arriverait abîmé au décodeur : là
    // où le navigateur ne sait pas transformer une piste reçue, on ne la reçoit
    // pas du tout.
    if (probe === null) {
      publication.setSubscribed(false);

      return;
    }

    this.probes.set(publication.trackSid, probe);
    this.emit();
  }

  private emit(): void {
    const livekit = this.livekit;
    const sink = this.sink;

    if (livekit === null || sink === null) {
      return;
    }

    sink.sources(this.snapshot(livekit));
  }

  /** Les micros de la salle, de la plus ancienne à la plus récente ; les flux et mesures des pistes parties oubliés. */
  private snapshot(livekit: LiveKitModule): AudioSource[] {
    const room = this.room;

    if (room === null) {
      return [];
    }

    const seen: { source: AudioSource; order: number }[] = [];
    const present = new Set<string>();
    const local = room.localParticipant;
    const localPublication = local.getTrackPublication(livekit.Track.Source.Microphone);
    const localTrack = localPublication?.audioTrack;

    if (localPublication !== undefined && localTrack !== undefined && this.microphone !== null) {
      const source = this.sourceFor(local, localPublication, localTrack.mediaStreamTrack, true);

      present.add(source.id);
      seen.push({ source, order: this.orderOf(source.id) });
    }

    for (const participant of room.remoteParticipants.values()) {
      const publication = participant.getTrackPublication(livekit.Track.Source.Microphone);
      const track = publication?.audioTrack;

      // Une piste reçue que le moteur n'a pas prise — refusée, ou pas encore
      // branchée — n'est pas une source.
      if (publication === undefined || track === undefined || !this.probes.has(publication.trackSid)) {
        continue;
      }

      const source = this.sourceFor(participant, publication, track.mediaStreamTrack, false);

      present.add(source.id);
      seen.push({ source, order: this.orderOf(source.id) });
    }

    for (const sid of Array.from(this.streams.keys())) {
      if (!present.has(sid)) {
        this.streams.delete(sid);
      }
    }

    for (const sid of Array.from(this.probes.keys())) {
      if (!present.has(sid)) {
        this.probes.delete(sid);
      }
    }

    return seen.sort((a, b) => a.order - b.order).map(({ source }) => source);
  }

  private sourceFor(
    participant: Attributed,
    publication: PublishedTrack,
    mediaStreamTrack: MediaStreamTrack,
    local: boolean,
  ): AudioSource {
    const sid = publication.trackSid;
    let held = this.streams.get(sid);

    // La piste reçue ne change pas quand l'émetteur remplace la sienne ; si elle
    // change quand même, le flux suit.
    if (held === undefined || held.stream.getAudioTracks()[0] !== mediaStreamTrack) {
      this.sequence += 1;
      held = { stream: new MediaStream([mediaStreamTrack]), order: held?.order ?? this.sequence };
      this.streams.set(sid, held);
    }

    return {
      id: sid,
      participant: participant.identity,
      owner: ownerOf(participant.identity, participant.attributes),
      ownerName: participant.name === undefined || participant.name === "" ? "Invité" : participant.name,
      profile: profileOfTrack(publication.trackName),
      local,
      muted: publication.isMuted,
      stream: held.stream,
    };
  }

  private orderOf(sid: string): number {
    return this.streams.get(sid)?.order ?? Number.MAX_SAFE_INTEGER;
  }
}

function candidateOf(participant: Attributed, publication: { trackSid: string }): AudioCandidate {
  return {
    id: publication.trackSid,
    participant: participant.identity,
    owner: ownerOf(participant.identity, participant.attributes),
  };
}
